// @flow

import React from "react";
import { localize } from "redux-i18n";

import config from "@config";
import type { PaginationExtendedType } from "@types";
import actionCreators from "@modules/employee/actionCreator";
import { GSGrid, Icons, Pagination, TextField, View } from "@components";
import type { StepProps } from "@components";
import type { SpecialContactEmployeeSearch } from "@modules/service/reducer/special-contacts/types";

type OwnProps = $Exact<{
  clientId: number,
  employeeList: Array<SpecialContactEmployeeSearch>,
  employeePagination: PaginationExtendedType,
  getEmployeeList: (params: Object) => {},
  clearEmployeeList: () => {},
}>;

type Props = $Exact<{
  ...OwnProps,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class Step2SelectEmployee extends React.Component<Props> {
  componentDidUpdate(prevProps: Props) {
    if (!this.props.editable && prevProps.editable !== this.props.editable) {
      this.props.clearEmployeeList();
    }
  }

  handlePaginationChange = (page: number) => {
    this.props.getEmployeeList({
      clientId: this.props.clientId,
      page,
    });
  };

  handleFilterSubmit = (event: SyntheticEvent<HTMLInputElement>) => {
    const criteria = event.currentTarget.value;

    if (criteria) {
      this.props.getEmployeeList({
        clientId: this.props.clientId,
        search: { last_name: event.currentTarget.value },
      });
      return;
    }

    this.props.clearEmployeeList();
  };

  handleSubmit = (employee: SpecialContactEmployeeSearch) => () => {
    if (this.props.handleSubmit) {
      this.props.handleSubmit(
        {
          id: employee.id,
          name: employee.name,
        },
        true,
      );
    }

    this.props.clearEmployeeList();
  };

  get columns() {
    const columnsConfig = [
      {
        key: "employeePrimaryReference",
        name: this.props.t("EMPLOYEE_REF"),
        align: "left",
      },
      {
        key: "name",
        name: this.props.t("EMPLOYEE_NAME"),
        align: "left",
      },
      {
        key: "department",
        name: this.props.t("DEPARTMENT"),
        align: "left",
        renderCell: ({ row }: { row: SpecialContactEmployeeSearch }) => {
          if ((row.organisationalUnits || {}).length) {
            return row.organisationalUnits.filter(ou => ou).join(", ");
          }

          return "";
        },
      },
      {
        key: "jobTitle",
        name: this.props.t("JOB_TITLE"),
        align: "left",
        renderCell: ({ row }: { row: SpecialContactEmployeeSearch }) => {
          if ((row.jobTitles || {}).length) {
            return row.jobTitles.filter(jt => jt).join(", ");
          }

          return "";
        },
      },
    ];

    if (this.props.editable) {
      columnsConfig.push({
        name: this.props.t("SELECT"),
        key: "actions",
        renderCell: ({ row }: { row: SpecialContactEmployeeSearch }) =>
          row.isSpecialContact ? (
            <div className="d-flex align-items-end">
              {this.props.t("ALREADY_A_SPECIAL_CONTACT")}
            </div>
          ) : (
            <div
              className="d-flex align-items-center text-charcoal-dark pointer"
              onClick={this.handleSubmit(row)}
            >
              <span>{this.props.t("SELECT")}</span>
              <Icons.ArrowRightIcon className="ml-1" />
            </div>
          ),
      });
    }

    return columnsConfig;
  }

  render() {
    if (!this.props.editable) {
      return <div />;
    }

    return (
      <View gap={4} alignItems="center">
        <View gap={1} width="40%">
          <label htmlFor="lastName" className="text-grey">
            {this.props.t("SEARCH_BY_SURNAME")}
          </label>
          <div className="input-icon-right">
            <TextField id="lastName" onChange={this.handleFilterSubmit} />
            <span className="material-icons">search</span>
          </div>
        </View>
        {this.props.employeeList ? (
          <View width="100%">
            <GSGrid rows={this.props.employeeList} columns={this.columns} />
          </View>
        ) : null}
        {this.props.employeePagination ? (
          <View width="100%" direction="row" justifyContent="center">
            <Pagination
              className="mx-auto d-block"
              currentPage={this.props.employeePagination.pageNumber}
              totalPages={this.props.employeePagination.totalPages}
              onChange={this.handlePaginationChange}
            />
          </View>
        ) : null}
      </View>
    );
  }
}

export default localize()(Step2SelectEmployee);
